import { Button } from "@bindery/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@bindery/ui/components/ui/dialog";
import { BookOpenIcon, PlusIcon } from "@heroicons/react/24/solid";
import { type RefObject, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";
import { chosenReaderStart, readerStart } from "@/auth/auto-start";
import type { ReaderStart } from "@/auth/enter";
import { ReaderEntryButton } from "@/auth/reader-entry-button";
import { DesignSkeleton } from "@/components/design-switcher/skeleton";
import { type SessionStore, sessionStore, useSession } from "@/convex/session";
import { timeSince } from "@/library/time-ago";
import { type DeviceReader, deviceReaders } from "@/storage/device-readers";
import { readNickname } from "@/storage/nickname";
import { SIGN_OUT_PATH } from "./sign-out";

export const CHOOSE_READER_PATH = "/choose-reader";

/**
 * A colour each reader keeps across visits, from their id, so a child who
 * has no nickname yet is still "the green reader" every time, and a named
 * one's initial sits on the same colour as last time. White on every hue
 * reads at 4.5:1 or better.
 */
export interface ReaderHue {
  name: string;
  className: string;
}

const RED: ReaderHue = { name: "red", className: "bg-flamenco-600" };

export const READER_HUES: readonly ReaderHue[] = [
  RED,
  { name: "blue", className: "bg-skyfall-700" },
  { name: "green", className: "bg-mintleaf-600" },
  { name: "purple", className: "bg-plumwood-600" },
  { name: "orange", className: "bg-orange-700" },
  { name: "teal", className: "bg-teal-700" },
  { name: "pink", className: "bg-pink-700" },
];

export function readerHue(readerId: string): ReaderHue {
  let hash = 0;
  for (const char of readerId) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  }
  return READER_HUES[hash % READER_HUES.length] ?? RED;
}

export interface ReaderChoice {
  readerId: string;
  /** The device's nickname for the reader, or "The green reader" when it has none. */
  label: string;
  named: boolean;
  hue: ReaderHue;
  /** How long since this device read as them; null for a reader kept from before it kept times. */
  lastRead: string | null;
  start: ReaderStart;
}

export interface ReaderChoices {
  readers: ReaderChoice[];
  someoneNew: ReaderStart;
}

export const unnamedReaderLabel = (hue: ReaderHue): string =>
  `The ${hue.name} reader`;

/**
 * Every reader the device offers, in the order it keeps them, each with the
 * round trip that resumes it by name, and the fresh start for whoever is
 * none of them. The nickname is all the picker shows of a reader: on a
 * family or classroom device whoever holds it may see who reads here, and
 * nothing more.
 */
export function readerChoices(
  readers: DeviceReader[],
  returnTo: { pathname: string; search: string },
  nicknameOf: (readerId: string) => string | null = readNickname,
  now: number = Date.now(),
): ReaderChoices {
  return {
    readers: readers.map(({ readerId, lastSeenAt }) => {
      const nickname = nicknameOf(readerId);
      const hue = readerHue(readerId);
      return {
        readerId,
        label: nickname ?? unnamedReaderLabel(hue),
        named: nickname !== null,
        hue,
        lastRead: lastSeenAt > 0 ? timeSince(lastSeenAt, now) : null,
        start: chosenReaderStart(returnTo, readerId),
      };
    }),
    someoneNew: readerStart(returnTo, null),
  };
}

// By code point, so a letter outside the Basic Multilingual Plane is not
// split into two halves.
const firstCharacter = (text: string): string => Array.from(text)[0] ?? "";

function Initial({ choice }: { choice: ReaderChoice }) {
  return (
    <span
      aria-hidden="true"
      className={`flex size-12 shrink-0 items-center justify-center rounded-full text-2xl font-semibold text-white ${choice.hue.className}`}
    >
      {choice.named ? (
        firstCharacter(choice.label).toLocaleUpperCase()
      ) : (
        <BookOpenIcon className="size-6" />
      )}
    </span>
  );
}

const ROW_CLASS =
  "h-auto min-h-touch-generous w-full justify-start gap-4 whitespace-normal rounded-2xl border-2 border-foreground/10 bg-card px-4 py-3 text-left text-card-foreground shadow-none hover:border-foreground/30 hover:bg-card active:scale-[0.99] focus-visible:border-foreground focus-visible:ring-foreground/20 motion-reduce:transition-none";

const HEADING_CLASS =
  "text-3xl font-semibold tracking-tight text-foreground outline-none sm:text-4xl";

export interface ReaderPickerProps {
  readers: DeviceReader[];
  returnTo: { pathname: string; search: string };
  nicknameOf?: (readerId: string) => string | null;
  now?: number;
}

function ReaderChoiceList({
  readers,
  returnTo,
  nicknameOf = readNickname,
  now,
}: ReaderPickerProps) {
  const [mountedAt] = useState(Date.now);
  const choices = readerChoices(
    readers,
    returnTo,
    nicknameOf,
    now ?? mountedAt,
  );
  return (
    <>
      {choices.readers.length > 0 && (
        <ul className="mt-8 flex flex-col gap-3">
          {choices.readers.map((choice) => (
            <li key={choice.readerId}>
              <ReaderEntryButton
                entry={choice.start.entry}
                variant="outline"
                className={ROW_CLASS}
              >
                <Initial choice={choice} />
                <span className="flex min-w-0 flex-col">
                  <span className="line-clamp-2 text-xl font-semibold wrap-anywhere">
                    {choice.label}
                  </span>
                  {choice.lastRead !== null && (
                    <span className="text-muted-foreground text-sm">
                      Read {choice.lastRead}
                    </span>
                  )}
                </span>
              </ReaderEntryButton>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-6">
        <ReaderEntryButton
          entry={choices.someoneNew.entry}
          variant="ghost"
          className={`${ROW_CLASS} border-foreground/25 text-foreground hover:bg-card/60 border-dashed bg-transparent`}
        >
          <span
            aria-hidden="true"
            className="border-foreground/30 flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-dashed"
          >
            <PlusIcon className="size-6" />
          </span>
          <span className="text-xl font-semibold">I&apos;m someone new</span>
        </ReaderEntryButton>
      </div>
    </>
  );
}

const QUESTION = "Which reader are you?";
const INSTRUCTION = "Tap your name to keep reading where you left off.";

// Focus lands on the question, not on <body>, when the picker takes over
// from a gate or a page, so a screen reader announces the ask.
function useFocusOnMount(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    ref.current?.focus();
  }, [ref]);
}

/**
 * "Which reader are you?" for a device that remembers several readers.
 * Children use it on their own, so each reader is one big row to tap, led
 * by the first letter of their name on their own colour, and the only
 * other way out is a fresh reader. Every row is a real form or link, so
 * the choice works with scripts off and the chosen reader's id rides the
 * POST body, never a URL.
 */
export function ReaderPicker(props: ReaderPickerProps) {
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);
  return (
    <main
      aria-labelledby={headingId}
      className="bg-skyfall-500/20 min-h-svh px-5 pt-24 pb-10 md:pt-44 md:pb-16"
    >
      <div className="mx-auto w-full max-w-md">
        <h1
          id={headingId}
          ref={heading}
          tabIndex={-1}
          className={HEADING_CLASS}
        >
          {QUESTION}
        </h1>
        <p className="text-muted-foreground mt-2 text-base">{INSTRUCTION}</p>
        <ReaderChoiceList {...props} />
      </div>
    </main>
  );
}

/**
 * The picker over a reader route, modal: while someone is between readers
 * the chrome behind it belongs to a reader they have declined or not yet
 * confirmed, and must not rename, reset or save that reader for them. It
 * cannot be dismissed: a choice or a fresh reader is the only way on.
 */
export function ReaderPickerDialog(props: ReaderPickerProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          heading.current?.focus();
        }}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="bg-background inset-0 z-[80] block max-w-none translate-x-0 translate-y-0 gap-0 overflow-y-auto rounded-none border-0 p-0 shadow-none data-[state=open]:animate-none sm:max-w-none"
      >
        <div className="bg-skyfall-500/20 min-h-full px-5 pt-24 pb-10 md:pt-44 md:pb-16">
          <div className="mx-auto w-full max-w-md">
            <DialogTitle asChild>
              <h1 ref={heading} tabIndex={-1} className={HEADING_CLASS}>
                {QUESTION}
              </h1>
            </DialogTitle>
            <DialogDescription className="mt-2 text-base">
              {INSTRUCTION}
            </DialogDescription>
            <ReaderChoiceList {...props} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const LIBRARY = { pathname: "/reader", search: "" };

// An account's session is not swapped for a device reader behind its back:
// a chosen reader would replace it without the sign-out that flushes and
// warns.
function SignedInAsAccount() {
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);
  return (
    <main
      aria-labelledby={headingId}
      className="bg-skyfall-500/20 flex min-h-svh flex-col items-center justify-center px-5 py-10 text-center"
    >
      <h1 id={headingId} ref={heading} tabIndex={-1} className={HEADING_CLASS}>
        You&apos;re signed in
      </h1>
      <p className="text-muted-foreground mt-3 max-w-md text-base">
        To read as another reader on this device, sign out of your account
        first.
      </p>
      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
        <Button asChild size="lg">
          <Link to={SIGN_OUT_PATH}>Sign out</Link>
        </Button>
        <Button asChild variant="outline" size="lg">
          <Link to={LIBRARY.pathname}>Back to the library</Link>
        </Button>
      </div>
    </main>
  );
}

/** The picker as its own page, landing in the library. */
export function ChooseReaderPage({
  store = sessionStore,
}: {
  store?: SessionStore;
}) {
  const session = useSession(store);
  if (session.status === "loading") return <DesignSkeleton />;
  if (session.status === "ready" && session.session.lane === "user_session") {
    return <SignedInAsAccount />;
  }
  return <ReaderPicker readers={deviceReaders()} returnTo={LIBRARY} />;
}
